import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle, AlertCircle, Info, X, XCircle } from 'lucide-react';

/**
 * Toast 浮动提醒组件
 * @param {Object} props
 * @param {string} props.id - 唯一标识
 * @param {string} props.message - 提醒消息
 * @param {string} props.type - 类型: success | error | warning | info
 * @param {number} props.duration - 持续时间（毫秒），默认3000
 * @param {Function} props.onClose - 关闭回调
 */
const Toast = ({ id, message, type = 'info', duration = 3000, onClose }) => {
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(100);
  const timerRef = useRef(null);
  const startTimeRef = useRef(Date.now());
  const remainingTimeRef = useRef(duration);

  // Toast 样式配置
  const config = {
    success: {
      icon: CheckCircle,
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-800',
      iconColor: 'text-green-500',
      progressColor: 'bg-green-500'
    },
    error: {
      icon: XCircle,
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      textColor: 'text-red-800',
      iconColor: 'text-red-500',
      progressColor: 'bg-red-500'
    },
    warning: {
      icon: AlertCircle,
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200',
      textColor: 'text-yellow-800',
      iconColor: 'text-yellow-500',
      progressColor: 'bg-yellow-500'
    },
    info: {
      icon: Info,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-800',
      iconColor: 'text-blue-500',
      progressColor: 'bg-blue-500'
    }
  };

  const currentConfig = config[type] || config.info;
  const Icon = currentConfig.icon;

  // 启动计时器
  const startTimer = () => {
    startTimeRef.current = Date.now();
    
    const updateProgress = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = Math.max(0, remainingTimeRef.current - elapsed);
      const progressValue = (remaining / duration) * 100;
      
      setProgress(progressValue);
      
      if (remaining <= 0) {
        onClose(id);
      } else {
        timerRef.current = requestAnimationFrame(updateProgress);
      }
    };
    
    timerRef.current = requestAnimationFrame(updateProgress);
  };

  // 暂停计时器
  const pauseTimer = () => {
    if (timerRef.current) {
      cancelAnimationFrame(timerRef.current);
      const elapsed = Date.now() - startTimeRef.current;
      remainingTimeRef.current = Math.max(0, remainingTimeRef.current - elapsed);
    }
  };

  // 鼠标进入 - 暂停
  const handleMouseEnter = () => {
    setIsPaused(true);
    pauseTimer();
  };

  // 鼠标离开 - 继续
  const handleMouseLeave = () => {
    setIsPaused(false);
    startTimer();
  };

  // 初始化和清理
  useEffect(() => {
    startTimer();
    
    return () => {
      if (timerRef.current) {
        cancelAnimationFrame(timerRef.current);
      }
    };
  }, []);

  return (
    <div
      className={`
        ${currentConfig.bgColor} ${currentConfig.borderColor} ${currentConfig.textColor}
        relative overflow-hidden rounded-xl border shadow-lg
        min-w-[300px] max-w-[400px]
        transform transition-all duration-300 ease-out
        animate-slideInRight
      `}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* 进度条 */}
      <div className="absolute bottom-0 left-0 h-1 bg-gray-200 w-full">
        <div
          className={`h-full ${currentConfig.progressColor} transition-all duration-100 ease-linear`}
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="flex items-start gap-3 p-4 pr-10">
        {/* 图标 */}
        <Icon className={`${currentConfig.iconColor} flex-shrink-0 mt-0.5`} size={20} />
        
        {/* 消息内容 */}
        <div className="flex-1 text-sm font-medium leading-relaxed">
          {message}
        </div>

        {/* 关闭按钮 */}
        <button
          onClick={() => onClose(id)}
          className={`
            absolute top-3 right-3
            ${currentConfig.textColor} hover:opacity-70
            transition-opacity duration-200
            p-1 rounded-full hover:bg-black/5
          `}
          aria-label="关闭"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

export default Toast;
